/**
 * Loads a PDF via pdf.js and exposes a `PageRenderer` that the viewer
 * shell can call to draw individual pages onto canvases.
 *
 * Page intrinsic dimensions are read from the PDF's default viewport at
 * scale=1, rotation=0 — exactly the canonical "document space" used by
 * overlay coordinates.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  getDocument,
  type PDFDocumentLoadingTask,
  type PDFDocumentProxy,
} from 'pdfjs-dist'

import { configurePdfWorker } from './pdfWorker'
import type { PageModel, PageRenderer, Rotation } from './types'

interface PdfDocState {
  pages: PageModel[]
  loading: boolean
  error: Error | null
  // Held internally so renderPage can fetch page proxies on demand.
  doc: PDFDocumentProxy | null
}

const INITIAL: PdfDocState = {
  pages: [],
  loading: true,
  error: null,
  doc: null,
}

/**
 * Loads a PDF from either a URL or an in-memory ``Blob``. The Blob path is
 * what the live-preview flow uses: we hand the bytes straight to pdf.js as
 * ``getDocument({ data })`` so the worker never tries to HTTP-GET a
 * ``blob:`` URL — that route 405s on some staging stacks and falls back to
 * the "Failed to load PDF." error slot.
 */
export function usePdfDocument(source: string | Blob | null): PageRenderer {
  const [state, setState] = useState<PdfDocState>(INITIAL)
  const [sourceVersion, setSourceVersion] = useState(0)

  // Reset state to "loading" the moment the source prop changes. Doing this
  // during render (vs. inside the effect) avoids the rule against
  // synchronous setState in an effect body, and matches the project's
  // useLivePreview pattern for resetting derived state on prop change.
  // Identity comparison works for both branches: URL strings compare by
  // value, Blob references compare by identity (stable across renders).
  const [prevSource, setPrevSource] = useState<string | Blob | null>(source)
  const nextSourceVersion =
    prevSource === source ? sourceVersion : sourceVersion + 1
  if (prevSource !== source) {
    setPrevSource(source)
    setSourceVersion(nextSourceVersion)
    setState(
      source
        ? {
            // Keep the last successfully rendered PDF on screen while a
            // live-preview replacement blob is being parsed. The detail
            // route's Updating Preview overlay then covers the stale frame
            // instead of the whole viewer collapsing to a spinner.
            pages: state.pages,
            loading: true,
            error: null,
            doc: state.doc,
          }
        : INITIAL,
    )
  }

  useEffect(() => {
    configurePdfWorker()
    if (!source) return

    let cancelled = false
    let task: PDFDocumentLoadingTask | null = null

    const run = async () => {
      try {
        if (typeof source === 'string') {
          task = getDocument({ url: source })
        } else {
          // Blob → ArrayBuffer → pdf.js. Reading the Blob is async, so we
          // open the loading task inside `run` instead of synchronously —
          // the cleanup below uses the captured `task` ref.
          const buffer = await source.arrayBuffer()
          if (cancelled) return
          task = getDocument({ data: new Uint8Array(buffer) })
        }
        const doc = await task.promise
        if (cancelled) {
          doc.destroy().catch(() => {})
          return
        }
        const pages: PageModel[] = []
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i)
          if (cancelled) {
            doc.destroy().catch(() => {})
            return
          }
          const viewport = page.getViewport({ scale: 1, rotation: 0 })
          pages.push({
            id: `pdf-${nextSourceVersion}-page-${i}`,
            index: i - 1,
            pageNumber: i,
            intrinsic: {
              width: viewport.width,
              height: viewport.height,
            },
          })
        }
        if (!cancelled) {
          setState({ pages, loading: false, error: null, doc })
        }
      } catch (err) {
        if (cancelled) return
        const error =
          err instanceof Error ? err : new Error(String(err ?? 'Unknown error'))
        setState((prev) =>
          prev.pages.length > 0 && prev.doc
            ? { ...prev, loading: false, error }
            : {
                pages: [],
                loading: false,
                error,
                doc: null,
              },
        )
      }
    }
    void run()

    return () => {
      cancelled = true
      task?.destroy().catch(() => {})
    }
  }, [source, nextSourceVersion])

  // Tear down the PDFDocumentProxy when the URL changes (state.doc is
  // replaced on the next successful load).
  useEffect(() => {
    const current = state.doc
    return () => {
      current?.destroy().catch(() => {})
    }
  }, [state.doc])

  const renderer = useMemo<PageRenderer>(() => {
    const renderPage: PageRenderer['renderPage'] = async ({
      page,
      canvas,
      scale,
      rotation,
      devicePixelRatio,
    }) => {
      const doc = state.doc
      if (!doc) throw new Error('PDF document is not loaded')

      const pdfPage = await doc.getPage(page.pageNumber)
      // pdf.js bakes rotation + scale into the viewport. We use the
      // device-pixel-ratio-scaled viewport for the canvas bitmap, then
      // shrink the canvas back down via CSS pixels in the consumer.
      const cssViewport = pdfPage.getViewport({
        scale,
        rotation: rotation as Rotation,
      })
      const renderViewport = pdfPage.getViewport({
        scale: scale * devicePixelRatio,
        rotation: rotation as Rotation,
      })
      canvas.width = Math.max(1, Math.floor(renderViewport.width))
      canvas.height = Math.max(1, Math.floor(renderViewport.height))
      canvas.style.width = `${Math.floor(cssViewport.width)}px`
      canvas.style.height = `${Math.floor(cssViewport.height)}px`

      const ctx = canvas.getContext('2d', { alpha: false })
      if (!ctx) throw new Error('Canvas 2D context unavailable')

      const task = pdfPage.render({
        canvas,
        canvasContext: ctx,
        viewport: renderViewport,
      })
      await task.promise
    }

    return {
      pages: state.pages,
      loading: state.loading,
      error: state.error,
      renderPage,
    }
  }, [state.pages, state.loading, state.error, state.doc])

  return renderer
}
